import Axios from './axiosClient'
import { API_ENDPOINT } from '../constants/apis'
import { AxiosResponse } from 'axios'
import type { FeatureFlagStatus } from '../constants/featureFlagStatus'

interface GetFeatureFlagsParams {
  searchParam?: string
  limit?: number
}

export type FeatureFlagSyncEnvironment = 'dev-01' | 'dev-02' | 'prod-eu'
export type FeatureFlagDefinitionLevel = 'organization' | 'repository' | 'team'
export type FeatureFlagSyncMode = 'preview' | 'apply'

export type FeatureFlagFieldDifference =
  | {
      field: 'status'
      prod_value: FeatureFlagStatus | null
      target_value: FeatureFlagStatus | null
    }
  | {
      field: 'subtext'
      prod_value: string | null
      target_value: string | null
    }

export interface FeatureFlagDifference {
  name: string
  definition_level: FeatureFlagDefinitionLevel
  fields: FeatureFlagFieldDifference[]
}

export interface MissingFeatureFlag {
  name: string
  definition_level: FeatureFlagDefinitionLevel
}

export interface FeatureFlagSyncTargetResult {
  environment: FeatureFlagSyncEnvironment
  outcome: 'success' | 'failed'
  compared: number
  updated: number
  unchanged: number
  differences: FeatureFlagDifference[]
  missing: MissingFeatureFlag[]
  error?: string | null
}

export interface FeatureFlagSyncResult {
  mode: FeatureFlagSyncMode
  trigger: 'manual' | 'daily'
  targets: FeatureFlagSyncTargetResult[]
}

// Get feature flags for autocomplete or adoption data
export const getFeatureFlags = ({
  searchParam = '',
  limit = 20
}: GetFeatureFlagsParams = {}): Promise<AxiosResponse<any>> => {
  return Axios.get(`${API_ENDPOINT}/feature_flags`, {
    params: {
      page: 1,
      limit,
      ...(searchParam ? { search_param: searchParam } : {})
    }
  })
}

// The paid orgs behind a flag's Enabled Orgs / Opted Out counts. Same paid-org
// definition as the counts, so the lists always add up to what the table shows.
export const getFeatureFlagOrganizations = (
  definitionId: number
): Promise<AxiosResponse<any>> => {
  return Axios.get(
    `${API_ENDPOINT}/feature_flags/${definitionId}/organizations`
  )
}

export const syncFeatureFlagMetadata = (
  mode: FeatureFlagSyncMode,
  environments?: FeatureFlagSyncEnvironment[],
  signal?: AbortSignal
): Promise<AxiosResponse<FeatureFlagSyncResult>> =>
  Axios.post(
    `${API_ENDPOINT}/feature_flags/sync/${mode}`,
    environments ? { environments } : undefined,
    { signal }
  )

// 'recent' -> DataDog (~2 weeks, Slack + UI changes), synchronous.
// 'full'   -> CloudWatch (~1 year, Slack + UI), async: the first call returns a
// query_id; poll by passing it back until status === 'complete'.
export type FeatureFlagHistoryMode = 'recent' | 'full'

// organizationId undefined -> every org's changes to the flag (Feature Flags page).
export const getFeatureFlagHistory = (
  organizationId: number | string | undefined,
  featureFlagName: string,
  featureFlagId?: number | string,
  mode: FeatureFlagHistoryMode = 'recent',
  queryId?: string,
  signal?: AbortSignal
): Promise<AxiosResponse<any>> => {
  return Axios.post(
    `${API_ENDPOINT}/lookup/feature-flag-history`,
    {
      organization_id: organizationId != null ? Number(organizationId) : null,
      feature_flag_name: featureFlagName,
      feature_flag_id: featureFlagId != null ? String(featureFlagId) : null,
      mode,
      query_id: queryId ?? null
    },
    { signal }
  )
}
